import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Loader2, X } from 'lucide-react'
import { useAppStore } from '@core/store'
import { useAuth } from '@core/auth/useAuth'
import { sessoesApi, type PedidoDeSessao } from './sessoesApi'
import { textoDoPedido } from './sessoesRegras'
import {
  abrirPedidosDeSessao,
  fecharPedidosDeSessao,
  sessoesMudaram,
  useJanelaPedidos,
} from './sessaoPartilhadaEstado'

const BOTAO =
  'rounded border border-[var(--color-border)] px-2 py-0.5 text-[11px] text-[var(--color-text-muted)] hover:text-[var(--color-text)] disabled:opacity-40'

function mensagem(err: unknown): string {
  return err instanceof Error ? err.message : 'Não foi possível falar com o servidor.'
}

/**
 * Os pedidos de autorização: alguém incluiu uma peça minha (um BCF que
 * registei, a minha triagem, um modelo meu) na sessão que partilhou, e
 * só eu a posso partilhar. Autorizar dá acesso a quem está na sessão;
 * recusar tira a peça da sessão.
 *
 * Abre pelo aviso (`?pedidos_sessao=1` no endereço) ou pela lista
 * "Sessões partilhadas". Montado uma vez no layout do workspace.
 */
export default function JanelaPedidosDeSessao() {
  const aberta = useJanelaPedidos()
  const { mode, workspace, user } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const pedidoNoEndereco = new URLSearchParams(location.search).has('pedidos_sessao')
  const pronto = mode === 'cloud' && !!workspace && !!user

  // A ligação do aviso: abre a janela e tira o parâmetro do endereço.
  useEffect(() => {
    if (!pedidoNoEndereco || !pronto) return
    const resto = new URLSearchParams(location.search)
    resto.delete('pedidos_sessao')
    const procura = resto.toString()
    navigate(`${location.pathname}${procura ? `?${procura}` : ''}`, { replace: true })
    abrirPedidosDeSessao()
  }, [pedidoNoEndereco, pronto, location.pathname, location.search, navigate])

  if (!aberta || !pronto) return null
  return <Janela />
}

function Janela() {
  const pushToast = useAppStore((s) => s.pushToast)
  const [lista, setLista] = useState<PedidoDeSessao[] | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [ocupado, setOcupado] = useState<string | null>(null)
  const [ler, setLer] = useState(0)

  useEffect(() => {
    let vivo = true
    sessoesApi
      .pedidos()
      .then((l) => {
        if (vivo) setLista(l)
      })
      .catch((err) => {
        if (vivo) setErro(mensagem(err))
      })
    return () => {
      vivo = false
    }
  }, [ler])

  const responder = async (p: PedidoDeSessao, autorizar: boolean) => {
    const chave = `${p.sessao_id}:${p.peca_id}`
    setOcupado(chave)
    try {
      if (autorizar) await sessoesApi.autorizar(p.sessao_id, p.peca_id)
      else await sessoesApi.recusar(p.sessao_id, p.peca_id)
      pushToast(
        autorizar
          ? `Autorizado: "${p.peca_nome ?? 'a peça'}" passa a estar na sessão "${p.sessao_nome ?? ''}".`
          : `Recusado: "${p.peca_nome ?? 'a peça'}" saiu da sessão "${p.sessao_nome ?? ''}".`,
        { type: 'success' },
      )
      sessoesMudaram()
      setLer((n) => n + 1)
    } catch (err) {
      pushToast(mensagem(err), { type: 'error' })
    } finally {
      setOcupado(null)
    }
  }

  // Banda dos diálogos (CAMADA.dialogos), ver core/layout/camadas.ts.
  return (
    <div
      className="fixed inset-0 z-[1000] flex items-start justify-center bg-black/50 p-4 pt-16"
      role="dialog"
      aria-modal="true"
      aria-label="Pedidos de autorização"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) fecharPedidosDeSessao()
      }}
    >
      <div className="flex max-h-[80vh] w-full max-w-md flex-col gap-2 overflow-y-auto rounded-lg border border-[var(--color-border)] bg-[var(--color-panel)] p-4 text-xs text-[var(--color-text)] shadow-xl">
        <div className="flex items-start gap-2">
          <div className="min-w-0 flex-1">
            <h2 className="text-sm font-medium">Pedidos de autorização</h2>
            <p className="text-[var(--color-text-dim)]">
              Peças suas que outras pessoas incluíram nas sessões que partilharam. Só o dono de uma
              peça a pode partilhar.
            </p>
          </div>
          <button
            type="button"
            onClick={fecharPedidosDeSessao}
            aria-label="Fechar"
            className="rounded p-0.5 text-[var(--color-text-dim)] hover:text-[var(--color-text)]"
          >
            <X size={14} />
          </button>
        </div>
        {erro && <p className="text-[var(--color-error-text)]">{erro}</p>}
        {lista === null && !erro && (
          <p className="flex items-center gap-1 text-[var(--color-text-dim)]">
            <Loader2 size={12} className="animate-spin" aria-hidden /> A ler os pedidos…
          </p>
        )}
        {lista !== null && lista.length === 0 && (
          <p className="text-[var(--color-text-muted)]">Não há pedidos por responder.</p>
        )}
        {(lista ?? []).map((p) => {
          const chave = `${p.sessao_id}:${p.peca_id}`
          return (
            <div key={chave} className="flex flex-col gap-1.5 rounded border border-[var(--color-border-subtle)] p-2">
              <p className="text-[var(--color-text)]">{textoDoPedido(p)}</p>
              <p className="text-[10px] text-[var(--color-text-dim)]">
                Autorizar dá acesso enquanto a peça estiver na sessão. Para o tirar depois, use
                "Retirar a partilha" na própria peça.
              </p>
              <div className="flex gap-1.5">
                <button
                  type="button"
                  disabled={ocupado !== null}
                  onClick={() => void responder(p, true)}
                  className="rounded bg-[var(--color-accent)] px-2 py-0.5 text-[11px] text-white disabled:opacity-40"
                >
                  Autorizar
                </button>
                <button
                  type="button"
                  disabled={ocupado !== null}
                  onClick={() => void responder(p, false)}
                  className={BOTAO}
                >
                  Recusar
                </button>
                {ocupado === chave && <Loader2 size={12} className="animate-spin self-center" aria-hidden />}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
